'use client'

// React Imports
import { useState } from 'react'
import type { FormEvent } from 'react'

// Next Imports
import { useRouter } from 'next/navigation'

// MUI Imports
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Button from '@mui/material/Button'
import Alert from '@mui/material/Alert'
import Chip from '@mui/material/Chip'

// Third-party Imports
import classnames from 'classnames'
import { TVStorApiError } from 'trantor-tvstor-api-js'
import type { IdentifyResult } from 'trantor-tvstor-api-js'

// Component Imports
import Link from '@components/Link'
import Logo from '@components/layout/shared/Logo'
import LanguageDropdown from '@components/layout/shared/LanguageDropdown'
import ForcePasswordChangeDialog from '@components/user/ForcePasswordChangeDialog'
import { brandify } from '@components/Brand'
import CustomTextField from '@core/components/mui/TextField'

// Config Imports
import themeConfig from '@configs/themeConfig'

// Hook Imports
import { useSettings } from '@core/hooks/useSettings'
import { useTranslation } from '@/i18n'
import { useExitTransition } from '@components/useExitTransition'

// Util Imports
import { apiClient, apiClientWithToken } from '@/libs/api'
import { setSession } from '@/utils/session'
import type { WebuiSession } from '@/utils/session'

// Styled Custom Components
const LoginBackground = styled('div')({
  backgroundImage: 'url(/images/pages/login-background.jpg)',
  backgroundSize: 'cover',
  backgroundPosition: 'center'
})

// IDENTIFIER-FIRST login (ratified 2026-07-31, the Google/Entra pattern). Three
// steps, and the split is not cosmetic:
//
// #118 NAS-QUALNAMES (ratifica Luca 2026-08-14 §2): step 1 no longer accepts a
// BARE name — the identifier must carry its realm (`REALM\user`, `user@realm`),
// `local\root` for the break-glass. The field teaches the form up front and the
// 422 repeats it; nothing here derives a realm from anything.
//
//   1) 'username' — POST /auth/identify resolves the REALM from the domain. The
//      answer says nothing about whether the account exists (an unknown name in a
//      password realm gets exactly the same one), and this page must not leak it
//      either: no branch, no different rendering, no early failure. Whatever comes
//      back, step 2 renders identically and only ITS submit can fail.
//   2) 'password' — the password for the realm just resolved, named on screen.
//
//   3) 'otp' — #181 otp-web (ratifica Luca 2026-08-19 §4): the second factor
//      of the ALREADY-ENROLLED credential. Reached only when the password step
//      answered `401 mfa_required` — minted by the backend strictly AFTER the
//      password verified (auth.yaml), so the step's very existence leaks
//      nothing to whoever does not own the password. The submit re-presents
//      the SAME login with the `otp` field (the CLI's `--otp` shape: a 6-digit
//      TOTP code or one single-use recovery code, told apart server-side).
//      Enrolment (QR/secret) stays out — ondata 1b: `mfa_enrollment_required`
//      remains a truthful message, no new surfaces beyond the code field.
type Step = 'username' | 'password' | 'otp'

const Login = () => {
  // States
  const [isPasswordShown, setIsPasswordShown] = useState(false)
  const [step, setStep] = useState<Step>('username')
  const [username, setUsername] = useState('')
  const [identity, setIdentity] = useState<IdentifyResult | null>(null)
  const [password, setPassword] = useState('')
  const [otp, setOtp] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // Login accepted but mustChangePassword is set: the session is parked here (no
  // cookie yet) until the forced change succeeds — or the user aborts the login.
  const [pendingChange, setPendingChange] = useState<WebuiSession | null>(null)

  // Hooks
  const router = useRouter()
  const { settings } = useSettings()
  const { t } = useTranslation()

  // Exit-animation sweep (Luca 2026-07-24): the parked session is retained
  // through the fade so the dialog dissolves whole when the login is aborted.
  const pendingExit = useExitTransition(pendingChange)

  const handleClickShowPassword = () => setIsPasswordShown(show => !show)

  // Back to step 1 to fix a typo: the realm and everything typed after it go with
  // it — the next identify must decide from scratch.
  const restart = () => {
    setStep('username')
    setIdentity(null)
    setPassword('')
    setOtp('')
    setError(null)
  }

  // GUI-PARITÀ ondata 0 (#181 T2-T4): the refusals are told apart by the
  // problem CODE the backend already sends (auth.yaml), never by status alone
  // — a collapsed 401 told an MFA user the password was wrong, a collapsed
  // 503 sent an AD outage to look at the cluster store, and a collapsed 422
  // told an LDAP user to type the realm they had just typed.
  const describeError = (err: unknown): string => {
    if (err instanceof TVStorApiError && err.status === 401) {
      // (`mfa_required` never reaches here: enter() intercepts it and opens
      // the OTP step — #181 otp-web. The code below is minted only after the
      // password check too, so it is never an oracle on which accounts exist.)
      if (err.code === 'mfa_enrollment_required') {
        return t('login.mfaEnrollmentRequired')
      }

      // The uniform refusal: wrong user, wrong password, disabled/locked.
      return t('login.invalidCredentials')
    }

    // #74 MFA-1: an ADMINISTRATOR error (external MFA declared but unusable),
    // named so it can be repaired — never a silent password-only fallback.
    if (err instanceof TVStorApiError && err.status === 409 && err.code === 'mfa_not_configured') {
      return t('login.mfaNotConfigured')
    }

    if (err instanceof TVStorApiError && err.status === 422) {
      // NAS-2 Fase 6: the ldap-realm web login is not built — this 422 is
      // about the REALM, not about the shape of the typed name.
      if (err.code === 'directory_login_not_supported') {
        return t('login.directoryLoginNotSupported')
      }

      // #118 NAS-QUALNAMES (ratifica Luca 2026-08-14 §2): the bare-name
      // refusal — about the SHAPE of what was typed, never about the account,
      // so repeating it here leaks nothing. Localized rather than echoed.
      return t('login.qualifiedNameRequired')
    }

    if (err instanceof TVStorApiError && err.status === 429) {
      return t('login.tooManyAttempts')
    }

    if (err instanceof TVStorApiError && err.status === 503) {
      // Three DIFFERENT outages (auth.yaml): the break-glass advice is right
      // only for the store one; an unreachable directory or MFA server fails
      // closed and simply needs a retry once it answers.
      if (err.code === 'directory_unreachable') {
        return t('login.directoryUnreachable')
      }

      if (err.code === 'mfa_unavailable') {
        return t('login.mfaUnavailable')
      }

      if (err.code === 'store_unavailable') {
        return t('login.realmUnavailable')
      }

      return t('login.serviceUnavailable')
    }

    return t('login.genericError')
  }

  // Step 1. The failures here are transport ones (unreachable backend, rate
  // limit) plus the syntactic 422 of #118 (the name did not carry its realm):
  // the server answers a stranger and a real account identically, so nothing
  // about the account can be reported — and the 422 is decided before any
  // lookup, so it is not an exception to that.
  const handleIdentify = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setSubmitting(true)

    try {
      const resolved = await apiClient().auth.identify(username)

      // OIDC/SAML redirects are a later round: the M1 contract declares
      // method 'password' only, so tvirt's defensive redirect branch is
      // dropped here rather than fought past the narrower type (2026-08-02).
      setIdentity(resolved)
      setStep('password')
      setSubmitting(false)
    } catch (err) {
      setError(describeError(err))
      setSubmitting(false)
    }
  }

  // Steps 2 and 3 share ONE submit: the login is always the same verb, the
  // otp field simply rides along on the re-presentation (#181 otp-web) — the
  // contract shape the CLI already speaks with --otp (auth.yaml, api-js
  // auth.login).
  const enter = async (otpCode?: string) => {
    setError(null)
    setSubmitting(true)

    try {
      const realm = identity?.realm ?? ''
      const created = await apiClient().auth.login(username, realm === '' ? undefined : realm, password, otpCode)

      // WEBEXP-KIOSK (ratifica Luca 2026-08-05): a files-realm login answers
      // mode 'kiosk' — the cookie carries it so the layout mounts the kiosk
      // shell, and the landing is /browse instead of the platform home. The
      // flow above is IDENTICAL for every realm: no visible bifurcation.
      const webuiSession: WebuiSession = {
        token: created.token,
        username: created.user.username,
        realm: created.user.realm,
        displayName: created.user.displayName,
        ...(created.mode !== undefined ? { mode: created.mode } : {}),

        // MFA.2 (#181 ondata 1b): the enrolment warning the 201 carries —
        // parked verbatim in the cookie so the console keeps showing what the
        // server affirmed (the grace banner on Home and on the profile page).
        ...(created.mfaEnrollmentDeadline !== undefined ? { mfaEnrollmentDeadline: created.mfaEnrollmentDeadline } : {}),
        ...(created.mfaEnrollmentDaysLeft !== undefined ? { mfaEnrollmentDaysLeft: created.mfaEnrollmentDaysLeft } : {})
      }

      // The mustChangePassword gate (contract §users): everything except the session
      // endpoints and the self password change would 403 — force the change first.
      if (created.mustChangePassword === true) {
        setPendingChange(webuiSession)
        setSubmitting(false)

        return
      }

      // Session cookie (dies with the browser — ratified 2026-07-12): opaque tvss_
      // token plus the identity the dashboard shows in the user dropdown.
      setSession(webuiSession)
      router.push(created.mode === 'kiosk' ? '/browse' : themeConfig.homePageUrl)
    } catch (err) {
      // #181 otp-web: `401 mfa_required` — the password VERIFIED and the
      // credential has a second factor (auth.yaml mints the code only after
      // the check): open the code step instead of narrating an error.
      if (err instanceof TVStorApiError && err.status === 401 && err.code === 'mfa_required') {
        setStep('otp')
        setOtp('')
        setError(null)
        setSubmitting(false)

        return
      }

      // A code WAS sent and the answer is the uniform 401 (auth.yaml: a
      // wrong, replayed or spent otp is `unauthenticated`, indistinguishable
      // from a wrong password BY THE SERVER — but this password verified on
      // this very step's opening, so the honest reading names the code).
      if (
        otpCode !== undefined &&
        err instanceof TVStorApiError &&
        err.status === 401 &&
        err.code !== 'mfa_enrollment_required'
      ) {
        setError(t('login.otpRejected'))

        // A refused code is SPENT either way (wrong, expired or replayed —
        // auth.yaml): clear the field so the retry starts from a fresh one.
        setOtp('')
        setSubmitting(false)

        return
      }

      setError(describeError(err))
      setSubmitting(false)
    }
  }

  const handlePassword = async (e: FormEvent) => {
    e.preventDefault()
    await enter()
  }

  const handleOtp = async (e: FormEvent) => {
    e.preventDefault()
    await enter(otp.trim())
  }

  // What the user is authenticating AGAINST, in words: the realm plus the kind of
  // account it holds. `realmKind` is about the REALM, never about the user.
  const realmLabel = (): string => {
    const realm = identity?.realm ?? ''

    if (identity?.realmKind === 'cluster') {
      return `${realm} — ${t('login.realmKindCluster')}`
    }

    if (identity?.realmKind === 'local') {
      return `${realm} — ${t('login.realmKindLocal')}`
    }

    // NAS-2 (#181 T4 adjacent): the identify answer distinguishes `files`
    // (kiosk fileUser realm) and `directory` (AD/LDAP) — rendering them keeps
    // the gain of the «identify that stopped lying».
    if (identity?.realmKind === 'files') {
      return `${realm} — ${t('login.realmKindFiles')}`
    }

    if (identity?.realmKind === 'directory') {
      return `${realm} — ${t('login.realmKindDirectory')}`
    }

    return realm
  }

  return (
    <div className='flex bs-full justify-center'>
      <LoginBackground
        className={classnames('flex bs-full flex-1 min-bs-[100dvh] relative max-md:hidden', {
          'border-ie': settings.skin === 'bordered'
        })}
      />
      <div className='flex justify-center items-center bs-full bg-backgroundPaper !min-is-full p-6 md:!min-is-[unset] md:p-12 md:is-[480px]'>
        <Link className='login-logo absolute block-start-5 sm:block-start-[33px] inline-start-6 sm:inline-start-[38px]'>
          <Logo />
        </Link>
        <div className='absolute block-start-5 sm:block-start-[25px] inline-end-6'>
          <LanguageDropdown />
        </div>
        <div className='flex flex-col gap-6 is-full sm:is-auto md:is-full sm:max-is-[400px] md:max-is-[unset] mbs-11 sm:mbs-14 md:mbs-0'>
          <div className='flex flex-col gap-1'>
            <Typography variant='h4'>{brandify(t('login.welcome').replace('{app}', themeConfig.templateName))}</Typography>
            <Typography>{t('login.subtitle')}</Typography>
          </div>
          {step === 'username' && (
            <form noValidate autoComplete='off' onSubmit={handleIdentify} className='flex flex-col gap-5'>
              {error && <Alert severity='error'>{brandify(error)}</Alert>}
              <CustomTextField
                autoFocus
                fullWidth
                label={t('login.username')}
                placeholder={t('login.usernamePlaceholder')}
                helperText={t('login.usernameHint')}
                value={username}
                onChange={e => setUsername(e.target.value)}
                slotProps={{ htmlInput: { 'aria-label': 'login-username' } }}
              />
              <Button fullWidth variant='contained' type='submit' disabled={submitting || username.trim() === ''}>
                {t('login.continue')}
              </Button>
            </form>
          )}
          {step !== 'username' && (
            <div className='flex items-center gap-2'>
              <Chip label={realmLabel()} size='small' variant='tonal' color='primary' aria-label='login-realm' />
              <Button size='small' variant='text' aria-label='login-change-user' onClick={restart}>
                {t('login.changeUser')}
              </Button>
            </div>
          )}
          {step === 'password' && (
            <form noValidate autoComplete='off' onSubmit={handlePassword} className='flex flex-col gap-5'>
              {error && <Alert severity='error'>{brandify(error)}</Alert>}
              <Typography color='text.primary' className='font-medium'>
                {username}
              </Typography>
              <CustomTextField
                autoFocus
                fullWidth
                label={t('login.password')}
                placeholder='············'
                id='outlined-adornment-password'
                type={isPasswordShown ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                slotProps={{
                  htmlInput: { 'aria-label': 'login-password' },
                  input: {
                    endAdornment: (
                      <InputAdornment position='end'>
                        <IconButton edge='end' onClick={handleClickShowPassword} onMouseDown={e => e.preventDefault()}>
                          <i className={isPasswordShown ? 'tabler-eye-off' : 'tabler-eye'} />
                        </IconButton>
                      </InputAdornment>
                    )
                  }
                }}
              />
              <div className='flex justify-end'>
                <Typography className='text-end' color='primary.main' component={Link}>
                  {t('login.forgotPassword')}
                </Typography>
              </div>
              <Button fullWidth variant='contained' type='submit' disabled={submitting}>
                {t('login.submit')}
              </Button>
            </form>
          )}
          {step === 'otp' && (
            <form noValidate autoComplete='off' onSubmit={handleOtp} className='flex flex-col gap-5'>
              {error && <Alert severity='error'>{brandify(error)}</Alert>}
              <Typography color='text.primary' className='font-medium'>
                {username}
              </Typography>
              <Typography>{t('login.otpPrompt')}</Typography>
              <CustomTextField
                autoFocus
                fullWidth
                label={t('login.otpLabel')}
                placeholder='123456'
                helperText={t('login.otpHint')}
                value={otp}
                onChange={e => setOtp(e.target.value)}
                slotProps={{
                  htmlInput: {
                    'aria-label': 'login-otp',

                    // The browser may offer a received/stored one-time code;
                    // maxLength matches the contract field (auth.yaml otp).
                    autoComplete: 'one-time-code',
                    maxLength: 64
                  }
                }}
              />
              <Button fullWidth variant='contained' type='submit' disabled={submitting || otp.trim() === ''}>
                {t('login.otpSubmit')}
              </Button>
            </form>
          )}
        </div>
      </div>
      {pendingExit.mounted !== null && (
        <ForcePasswordChangeDialog
          open={pendingExit.open}
          onExited={pendingExit.onExited}
          onCancel={() => setPendingChange(null)}
          onSubmit={async newPassword => {
            const parked = pendingExit.mounted

            if (parked === null) {
              return
            }

            // The login password just typed is the current one; the parked token
            // authenticates the self change, then the cookie lands and we enter.
            await apiClientWithToken(parked.token).users.setMyPassword(password, newPassword)
            setSession(parked)
            router.push(parked.mode === 'kiosk' ? '/browse' : themeConfig.homePageUrl)
          }}
        />
      )}
    </div>
  )
}

export default Login
